// Online-protokollen (DESIGN.md: Multiplayer-protokol). Delt mellem browseren og Room-DO'en, så
// begge sider validerer præcis ens. Ren TypeScript: ingen DOM og ingen Workers-typer.
//
// Modellen er autoritativ lockstep: DO'en kører sim'en og sender de intents, den har udført, som
// "ops" (et tick med intents, eller en handling udført i pause). Klienterne afspiller de samme ops
// på deres egen kopi, så al hændelsesdrevet UI virker som i solo. Et fuldt snapshot retter alt op
// ved join, reconnect, huller i rækken og forskel i hash.

import type { ActionKind, DifficultyId, Intent, PlayerId, State } from '../sim/types';

/** Protokolversion. Hæves, når beskederne ændrer form. */
export const PROTO = 1;

declare const __BUILD__: string | undefined;
/** Byggets stempel (vite.config: define). Klient og worker bygges sammen og skal være ens. */
export const BUILD: string = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev';

export const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const CODE_RE = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{4}$/;

/** Største besked fra en klient (bytes). Alt større er enten en fejl eller ondt i sinde. */
export const MAX_CLIENT_BYTES = 1024;
/** Er begge væk så længe, pauser rummet (ms). */
export const ABSENT_PAUSE_MS = 10_000;
/**
 * Har én spiller mistet forbindelsen så længe, pauser rummet også (ms). IGN-løkke runde 1: "min makker
 * røg af, og byerne gik i sort, mens hun var væk". Den, der er tilbage, kan selv trykke afspil.
 */
export const PARTNER_PAUSE_MS = 3_000;
/**
 * Spor: tillid (co-op runde 4: "under et årsskifte med en ny by gik to hjerter på 15-20 sekunder, mens
 * begge kiggede på noget andet"). Et nyt år starter, når I begge har trykket afspil. Har den ene trykket,
 * starter året af sig selv så længe efter (ms), så en makker, der har lagt telefonen, ikke holder banen.
 */
export const YEAR_READY_MS = 8_000;
/** Rummet ryddes efter så lang tid uden aktivitet (ms). */
export const ROOM_TTL_MS = 2 * 60 * 60 * 1000;
/** Ops sendes samlet så ofte (ms): 5 Hz. */
export const SEND_MS = 200;
/** Snapshot til SQLite så ofte (ms). */
export const SAVE_MS = 5000;
/** Watchdog-alarm så ofte (ms). */
export const WATCHDOG_MS = 10_000;
/** Hash med i hver n'te ops-ramme (cirka hvert 2. s). */
export const HASH_EVERY = 10;
/** Markøren højst så ofte (ms): 10 pr. s. */
export const CURSOR_MS = 100;

export type Seat = PlayerId;
export const SEATS: readonly Seat[] = ['p1', 'p2'];

/**
 * 'ask' (Spor: coop): "Byg her?" og de andre spørgsmål, man stiller makkeren om noget, kun den anden
 * rolle må. Pingen bærer handlingen (act), så makkeren læser præcis, hvad der bedes om.
 */
export const PING_KINDS = ['build', 'danger', 'mine', 'help', 'nice', 'ask'] as const;
export type PingKind = (typeof PING_KINDS)[number];
export type PingReply = 'yes' | 'no';

/** Alle handlinger, en klient må sende. Holdes i takt med ActionKind (tjekkes af typen nedenfor). */
export const ACTION_KINDS = [
  'build_line',
  'upgrade_line',
  'bury_line',
  'demolish',
  'reinforce_mast',
  'upgrade_station',
  'build_battery',
  'build_flywheel',
  'hire_crew',
  'dispatch_crew',
  'shoo_threat',
  'power_fcr',
  'power_norway',
  'power_shed',
  'pick_pack',
  'pick_both',
  'answer_card',
  'call_year',
  'trim_hz',
  'wait',
] as const satisfies readonly ActionKind[];
// Mangler en ActionKind i listen, fejler typetjekket her.
type MissingKind = Exclude<ActionKind, (typeof ACTION_KINDS)[number]>;
const _allKinds: MissingKind extends never ? true : never = true;
void _allKinds;

export const DIFFICULTY_IDS: readonly DifficultyId[] = ['laerling', 'vagthavende', 'systemansvarlig', 'orkan'];

/** Et punkt på kortet i verdens-km (samme for begge skærme, uanset størrelse). */
export type WorldP = [number, number];
/** Et ping peger på en node, ledning eller trussel (id) eller et punkt på kortet. */
export type PingAt = string | WorldP;

// ---------------------------------------------------------------------------
// Klient -> DO

export type ClientMsg =
  | { t: 'hello'; proto: number; build: string; room: string; token?: string }
  | { t: 'intent'; seq: number; kind: ActionKind; target?: string; opt?: string }
  | { t: 'ping'; kind: PingKind; at: PingAt; reply?: PingReply; to?: number; act?: ActionKind }
  | { t: 'setup'; level?: string; difficulty?: DifficultyId }
  /** Spor: coop: byt roller i rummet (begge må, kun i lobbyen). Værten bliver den samme person. */
  | { t: 'swap' }
  | { t: 'start' }
  | { t: 'ctl'; pause?: boolean; speed?: 1 | 2 }
  | { t: 'cursor'; p: WorldP | null }
  | { t: 'sync' }
  /** Efter banen: tilbage i rummet, evt. med en ny bane, og `go` starter med det samme. Begge sæder må. */
  | { t: 'again'; level?: string; go?: true }
  | { t: 'bye' };

// ---------------------------------------------------------------------------
// DO -> klient

/** En intent på ledningen: [spiller, clientSeq, kind, target?, opt?]. */
export type WireIntent = [Seat, number, ActionKind, string?, string?];
/** Et tick med intents ([0, ...]) eller handlinger udført i pause ([1, ...]). */
export type Op = [0, ...WireIntent[]] | [1, ...WireIntent[]];

/** 'year': et nyt år venter på, at I begge er klar (YEAR_READY_MS). */
export type Pause = 'player' | 'absent' | 'year' | null;

export interface SeatView {
  /** Forbundet lige nu. */
  on: boolean;
}

export interface RoomView {
  code: string;
  phase: 'lobby' | 'playing';
  level: string;
  difficulty: DifficultyId;
  /** Den, der vælger bane og starter (p1, eller p2 hvis p1 er gået for godt). */
  host: Seat;
  seats: Record<Seat, SeatView | null>;
  /**
   * Hvilken bane i rækken (tæller én op ved hver start). IGN-løkke 2, runde 3: "Prøv igen" startede en
   * ny bane uden lobby imellem, og klienterne troede, det var den gamle, så resultatkortet blev stående.
   */
  run: number;
}

/** not_host lukker ikke forbindelsen: makkeren prøvede noget, kun værten må (setup, start). */
export type ErrorCode = 'full' | 'version' | 'gone' | 'bad' | 'rate' | 'not_host';

export type ServerMsg =
  | { t: 'welcome'; you: Seat; token: string; lastSeq: number; room: RoomView }
  | { t: 'room'; room: RoomView }
  | { t: 'snap'; n: number; state: State; paused: Pause; speed: 1 | 2; ready?: Seat[] }
  | { t: 'ops'; n: number; ops: Op[]; h?: [number, string] }
  | { t: 'ack'; seq: number; ok: boolean; reason?: string }
  | { t: 'ping'; from: Seat; id: number; kind: PingKind; at: PingAt; reply?: PingReply; to?: number; act?: ActionKind }
  /** Efter et rollebyt: din nye plads. Tokenet er det samme, så det sendes ikke igen. */
  | { t: 'seat'; you: Seat; lastSeq: number; room: RoomView }
  | { t: 'cursor'; from: Seat; p: WorldP | null }
  /** ready: hvem der har sagt klar til det nye år (kun ved 'year'); go: ms til året starter af sig selv. */
  | { t: 'ctl'; paused: Pause; speed: 1 | 2; by?: Seat; ready?: Seat[]; go?: number }
  | { t: 'error'; code: ErrorCode };

// ---------------------------------------------------------------------------
// Validering

const ID_RE = /^[A-Za-z0-9_|:.-]{1,64}$/;
/** Et mål er et id, eller '*' for "alle" (Forstærk alle udsatte master). */
const TARGET_RE = /^(?:\*|[A-Za-z0-9_|:.-]{1,64})$/;
const OPT_RE = /^[A-Za-z0-9_.-]{1,32}$/;
const TOKEN_RE = /^[0-9a-f]{32}$/;
const BUILD_RE = /^[A-Za-z0-9_.-]{1,40}$/;
const LEVEL_RE = /^[a-z0-9-]{1,40}$/;
const WORLD_MAX = 100_000;

type Obj = Record<string, unknown>;

function isObj(v: unknown): v is Obj {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Kun de nævnte felter må være der (ingen ekstra felter sniger sig igennem). */
function onlyKeys(o: Obj, allowed: readonly string[]): boolean {
  for (const k of Object.keys(o)) if (!allowed.includes(k)) return false;
  return true;
}

function isInt(v: unknown, min: number, max: number): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
}

function isWorldP(v: unknown): v is WorldP {
  return (
    Array.isArray(v) &&
    v.length === 2 &&
    v.every((x) => typeof x === 'number' && Number.isFinite(x) && Math.abs(x) <= WORLD_MAX)
  );
}

function optStr(o: Obj, k: string, re: RegExp): boolean {
  return o[k] === undefined || (typeof o[k] === 'string' && re.test(o[k] as string));
}

/** Byte-længden af en streng i UTF-8 (uden TextEncoder, så den også virker i tests). */
export function utf8Bytes(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff) {
      n += 4;
      i++;
    } else n += 3;
  }
  return n;
}

/**
 * Pak og tjek en besked fra en klient. Alt, der ikke passer præcis, giver null: forkert type,
 * ekstra felter, for store tal, for lange strenge eller en besked over MAX_CLIENT_BYTES.
 */
export function parseClient(raw: string | ArrayBuffer): ClientMsg | null {
  if (typeof raw !== 'string') return null;
  if (raw.length > MAX_CLIENT_BYTES || utf8Bytes(raw) > MAX_CLIENT_BYTES) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  return validateClient(v);
}

export function validateClient(v: unknown): ClientMsg | null {
  if (!isObj(v) || typeof v.t !== 'string') return null;
  switch (v.t) {
    case 'hello':
      if (!onlyKeys(v, ['t', 'proto', 'build', 'room', 'token'])) return null;
      if (!isInt(v.proto, 0, 1000)) return null;
      if (typeof v.build !== 'string' || !BUILD_RE.test(v.build)) return null;
      if (typeof v.room !== 'string' || !CODE_RE.test(v.room)) return null;
      if (!optStr(v, 'token', TOKEN_RE)) return null;
      return v as ClientMsg;
    case 'intent':
      if (!onlyKeys(v, ['t', 'seq', 'kind', 'target', 'opt'])) return null;
      if (!isInt(v.seq, 1, 2 ** 31)) return null;
      if (typeof v.kind !== 'string' || !(ACTION_KINDS as readonly string[]).includes(v.kind)) return null;
      if (!optStr(v, 'target', TARGET_RE) || !optStr(v, 'opt', OPT_RE)) return null;
      return v as ClientMsg;
    case 'ping':
      if (!onlyKeys(v, ['t', 'kind', 'at', 'reply', 'to', 'act'])) return null;
      if (typeof v.kind !== 'string' || !(PING_KINDS as readonly string[]).includes(v.kind)) return null;
      if (v.act !== undefined && (v.kind !== 'ask' || typeof v.act !== 'string' || !(ACTION_KINDS as readonly string[]).includes(v.act))) return null;
      if (!(typeof v.at === 'string' ? ID_RE.test(v.at) : isWorldP(v.at))) return null;
      if (v.reply !== undefined && v.reply !== 'yes' && v.reply !== 'no') return null;
      if (v.to !== undefined && !isInt(v.to, 1, 2 ** 31)) return null;
      return v as ClientMsg;
    case 'setup':
      if (!onlyKeys(v, ['t', 'level', 'difficulty'])) return null;
      if (!optStr(v, 'level', LEVEL_RE)) return null;
      if (v.difficulty !== undefined && !DIFFICULTY_IDS.includes(v.difficulty as DifficultyId)) return null;
      return v as ClientMsg;
    case 'ctl':
      if (!onlyKeys(v, ['t', 'pause', 'speed'])) return null;
      if (v.pause !== undefined && typeof v.pause !== 'boolean') return null;
      if (v.speed !== undefined && v.speed !== 1 && v.speed !== 2) return null;
      return v as ClientMsg;
    case 'cursor':
      if (!onlyKeys(v, ['t', 'p'])) return null;
      if (v.p !== null && !isWorldP(v.p)) return null;
      return v as ClientMsg;
    case 'again':
      if (!onlyKeys(v, ['t', 'level', 'go'])) return null;
      if (!optStr(v, 'level', LEVEL_RE)) return null;
      if (v.go !== undefined && v.go !== true) return null;
      return v as ClientMsg;
    case 'start':
    case 'swap':
    case 'sync':
    case 'bye':
      if (!onlyKeys(v, ['t'])) return null;
      return v as ClientMsg;
    default:
      return null;
  }
}

/**
 * Pak en besked fra DO'en. Serveren er betroet, men klienten tjekker formen alligevel, så en
 * forkert besked aldrig vælter spillet.
 */
export function parseServer(raw: unknown): ServerMsg | null {
  if (typeof raw !== 'string' || raw.length > 400_000) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObj(v) || typeof v.t !== 'string') return null;
  switch (v.t) {
    case 'welcome':
      return (v.you === 'p1' || v.you === 'p2') && typeof v.token === 'string' && isInt(v.lastSeq, 0, 2 ** 31) && isObj(v.room) ? (v as ServerMsg) : null;
    case 'room':
      return isObj(v.room) ? (v as ServerMsg) : null;
    case 'seat':
      return (v.you === 'p1' || v.you === 'p2') && isInt(v.lastSeq, 0, 2 ** 31) && isObj(v.room) ? (v as ServerMsg) : null;
    case 'snap':
      return isInt(v.n, 0, 2 ** 40) && isObj(v.state) ? (v as ServerMsg) : null;
    case 'ops':
      return isInt(v.n, 0, 2 ** 40) && Array.isArray(v.ops) && v.ops.every((o) => Array.isArray(o) && (o[0] === 0 || o[0] === 1)) ? (v as ServerMsg) : null;
    case 'ack':
      return isInt(v.seq, 0, 2 ** 31) && typeof v.ok === 'boolean' ? (v as ServerMsg) : null;
    case 'ping':
      return (v.from === 'p1' || v.from === 'p2') && typeof v.kind === 'string' && (typeof v.at === 'string' || isWorldP(v.at)) && (v.act === undefined || typeof v.act === 'string') ? (v as ServerMsg) : null;
    case 'cursor':
      return (v.from === 'p1' || v.from === 'p2') && (v.p === null || isWorldP(v.p)) ? (v as ServerMsg) : null;
    case 'ctl':
      return v.speed === 1 || v.speed === 2 ? (v as ServerMsg) : null;
    case 'error':
      return typeof v.code === 'string' ? (v as ServerMsg) : null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Intents på ledningen

export function toWire(i: Intent): WireIntent {
  const w: WireIntent = [i.player, i.clientSeq, i.kind];
  if (i.target !== undefined || i.opt !== undefined) w.push(i.target ?? '');
  if (i.opt !== undefined) w.push(i.opt);
  return w;
}

export function fromWire(w: WireIntent): Intent {
  const i: Intent = { player: w[0], clientSeq: w[1], kind: w[2], source: 'human' };
  if (w[3] !== undefined && w[3] !== '') i.target = w[3];
  if (w[4] !== undefined) i.opt = w[4];
  return i;
}

export function opIntents(op: Op): Intent[] {
  const out: Intent[] = [];
  for (let k = 1; k < op.length; k++) out.push(fromWire(op[k] as WireIntent));
  return out;
}

/** Et ticks-op tæller som ét sim-tick; et pause-op udføres med det samme. */
export function isTickOp(op: Op): boolean {
  return op[0] === 0;
}

/** En ny rumkode ud fra fire tilfældige tal (0..1). */
export function makeCode(rand: () => number): string {
  let s = '';
  for (let i = 0; i < 4; i++) s += CODE_ALPHABET[Math.floor(rand() * CODE_ALPHABET.length) % CODE_ALPHABET.length];
  return s;
}

/** Rumkoden fra et link: ?rum=KODE eller /r/KODE. Små bogstaver og O/0-forvekslinger tåles ikke. */
export function codeFromUrl(pathname: string, search: string): string | null {
  const m = /^\/r\/([A-Za-z0-9]{4})\/?$/.exec(pathname);
  const raw = m?.[1] ?? new URLSearchParams(search).get('rum');
  if (!raw) return null;
  const c = normalizeCode(raw);
  return c;
}

/** Ryd en indtastet kode op: store bogstaver, ingen mellemrum. Ugyldige koder giver null. */
export function normalizeCode(raw: string): string | null {
  const c = raw.trim().toUpperCase().replace(/\s+/g, '');
  return CODE_RE.test(c) ? c : null;
}
